// src/Dashboard/pages/user/News/components/NewsCard.jsx

import React, { useMemo } from "react";
import {
  FaEdit,
  FaEye,
  FaShareAlt,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";

import { toAbsolutePhotoUrl } from "../../../../../services/newsPhotoServices";

import {
  FALLBACK_NEWS_IMAGE,
  formatNewsDate,
  getAuthorName,
  getNewsDate,
  makeExcerptHtml,
  onImgError,
} from "../utils/newsDashboardUtils";

export default function NewsCard({
  news,
  canModify,
  onRead,
  onEdit,
  onDelete,
}) {
  const coverSrc = useMemo(() => {
    return toAbsolutePhotoUrl(news?.image_url || "") || FALLBACK_NEWS_IMAGE;
  }, [news?.image_url]);

  const excerptHtml = useMemo(() => {
    const html = news?.content || "";
    const excerpt = makeExcerptHtml(html, 360, 4);

    return excerpt || `<p>Contenu vide.</p>`;
  }, [news?.content]);

  const createdAt = getNewsDate(news);
  const authorName = getAuthorName(news);

  const share = async () => {
    try {
      const url = `${window.location.origin}${window.location.pathname}#news-${news.id}`;
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié.");
    } catch {
      toast.error("Impossible de copier le lien.");
    }
  };

  return (
    <article
      id={`news-${news.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800 dark:bg-slate-950"
    >
      <button
        type="button"
        onClick={onRead}
        className="relative block w-full overflow-hidden bg-slate-100 text-left dark:bg-slate-900"
      >
        <div className="w-full" style={{ aspectRatio: "16 / 9" }}>
          <img
            src={coverSrc}
            alt={news?.title || "Illustration actualité"}
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            loading="lazy"
            onError={onImgError}
          />
        </div>

        {!news?.is_active && (
          <span className="absolute left-3 top-3 inline-flex rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white shadow-sm">
            Inactif
          </span>
        )}
      </button>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex-1">
          <h3 className="line-clamp-2 text-base font-bold leading-tight text-slate-900 dark:text-white sm:text-lg">
            {news?.title || "Sans titre"}
          </h3>

          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Publié le{" "}
            <span className="font-bold text-slate-700 dark:text-slate-200">
              {formatNewsDate(createdAt)}
            </span>
            {authorName && (
              <>
                {" "}
                par{" "}
                <span className="font-bold text-slate-700 dark:text-slate-200">
                  {authorName}
                </span>
              </>
            )}
          </p>

          <div
            className="mt-3 line-clamp-6 text-sm leading-6 text-slate-600 dark:text-slate-300 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1"
            dangerouslySetInnerHTML={{ __html: excerptHtml }}
          />
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onRead}
              className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-sm font-bold text-white transition hover:bg-indigo-800"
            >
              <FaEye />
              Lire
            </button>

            <button
              type="button"
              onClick={share}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
            >
              <FaShareAlt />
              Partager
            </button>
          </div>

          {canModify && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={onEdit}
                title="Modifier"
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
              >
                <FaEdit />
              </button>

              <button
                type="button"
                onClick={onDelete}
                title="Supprimer"
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50 text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
              >
                <FaTrash />
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}